import React, { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import qrcode from "../../../images/qr.png";

import TataReport from "../Template/tataReport/tatareport";
import CurewellReport from "../Template/CurewellReport/Curewell";
import DefaultReport from "../Template/DefaultReport/DefaultReport";

import tataVoucherPrint from "../Template/tataReport/tataVoucherPrient";
import curewellVoucherPrint from "../Template/CurewellReport/CurewellVoucherPrint";
import defaultVoucherPrint from "../Template/DefaultReport/defaultVoucherPrint";

import classNames from "classnames";
import styles from "./Invoice.module.scss";
import { Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { getInvoiceById } from "../../../store/slices/invoiceSlice";

const Invoice = () => {
  const dispatch = useDispatch();
  const [showInvoice, setShowInvoice] = useState(false);
  const invoiceRef = useRef();
  const { clinicInfo } = useSelector((state) => state.clinic);
  const clinic = clinicInfo && clinicInfo?.length > 0 ? clinicInfo[0] : [];
  const { invoiceInFo } = useSelector((state) => state.invoice);
  const invoiceId = sessionStorage.getItem("invoiceId");
  const { token } = useSelector((state) => state.auth);
  const [formData, setFormData] = useState({
    paidAmount: 0,
    totalAmount: 0,
    totalAfterDiscount: 0,
    dueAmount: 0,
  });

  // Selected report and printable address (used by templates)
  const reportName = clinic?.reportName || "default";
  const address = `
    ${clinic?.clinicAddress?.plot ? `${clinic.clinicAddress.plot}, ` : ""}
    ${clinic?.clinicAddress?.area ? `${clinic.clinicAddress.area}, ` : ""}
    ${clinic?.clinicAddress?.street ? `${clinic.clinicAddress.street}, ` : ""}
    ${
      clinic?.clinicAddress?.landMark ? `${clinic.clinicAddress.landMark}, ` : ""
    }
    ${
      clinic?.clinicAddress?.city?.name ? `${clinic.clinicAddress.city.name}, ` : ""
    }
    ${
      clinic?.clinicAddress?.state?.name ? `${clinic.clinicAddress.state.name}, ` : ""
    }
    ${
      clinic?.clinicAddress?.country?.name ? `${clinic.clinicAddress.country.name}, ` : ""
    }
    ${clinic?.clinicAddress?.pin ? clinic.clinicAddress.pin : ""}
  `.trim();

  useEffect(() => {
    if (invoiceId) {
      dispatch(getInvoiceById({ id: invoiceId, token }));
    }
  }, [dispatch, invoiceId, token]);

const downloadInvoice = async () => {
    try {
      const reportURL = `${window.location.origin}/view_invoice`;
      const qrDataUrl = await QRCode.toDataURL(reportURL);
      const extras = {
        address,
        watermark: clinic?.hospitalInfo?.waterMark || "",
        isoLogo: clinic?.hospitalInfo?.ISOLogo || "",
        footer: clinic?.hospitalInfo?.footer || "",
        // Added signature mapping for templates
        signature: clinic?.hospitalInfo?.authorizedSignature || clinic?.hospitalInfo?.signature || "", 
      };

      // MODIFICATION: Pass 'invoiceInFo' directly (the full root object).
      // Previously, using 'invoiceInFo?.appointmentDetails || invoiceInFo' stripped away
      // root-level data like Invoice ID and Date. 
      if (reportName === "tatareport") {
        tataVoucherPrint(clinic, qrDataUrl, invoiceInFo, null, extras);
      } else if (reportName === "curewellreport") {
        curewellVoucherPrint(clinic, qrDataUrl, invoiceInFo, null, extras);
      } else {
        defaultVoucherPrint(clinic, qrDataUrl, invoiceInFo, null, extras);
      }
    } catch (err) {
      console.error("downloadInvoice failed:", err);
      setShowInvoice(true);
      setTimeout(async () => {
        const input = invoiceRef.current;
        const html2canvasModule = await import("html2canvas");
        const html2canvasFn = html2canvasModule?.default || html2canvasModule;
        const jsPDFModule = await import("jspdf");
        const jsPDFLib = jsPDFModule?.default || jsPDFModule?.jsPDF || jsPDFModule;

        const canvas = await html2canvasFn(input, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
        });

        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDFLib("p", "mm", "a4");
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const imgProps = pdf.getImageProperties(imgData);
        const imgWidth = pageWidth;
        const imgHeight = (imgProps.height * imgWidth) / imgProps.width;

        let heightLeft = imgHeight;
        let position = 0;
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;

        while (heightLeft > 0) {
          position -= pageHeight;
          pdf.addPage();
          pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;
        }

        pdf.save("invoice.pdf");
        setShowInvoice(false);
      }, 500);
    }
  };
  useEffect(() => {
    if (!invoiceInFo) return;

    const appointmentDetails = invoiceInFo?.appointmentDetails || {};
    const healthScreeningItems =
      appointmentDetails?.healthScreeningDetails || [];
    const physiotherapyItems = appointmentDetails?.physiotherapyService || [];
    const radioServices = appointmentDetails?.radioServices || [];
    const healthPackageDetails = appointmentDetails?.healthPackageDetails || [];
    const allOtherServices = [
      ...(appointmentDetails?.tests || []),
      ...(appointmentDetails?.profiles || []),
      ...(appointmentDetails?.consultationDetails || []),
      ...(appointmentDetails?.skinServices || []),
      ...(appointmentDetails?.dentalServices || []),
      ...(appointmentDetails?.hairServices || []),
      ...(appointmentDetails?.generalServices || []),
    ];

    const isClient = invoiceInFo?.isClient === "Yes";

    // 🔹 Helper to safely get tax from item
    const getTaxFromItem = (item) => {
      if (!item || typeof item !== "object") return 0;

      if (typeof item?.tax === "number") return item.tax;

      for (const key of Object.keys(item)) {
        if (item[key] && typeof item[key] === "object" && "tax" in item[key]) {
          return Number(item[key].tax) || 0;
        }
      }
      return 0;
    };

    let totalAmount = 0,
      totalTax = 0;

    if (healthScreeningItems.length > 0) {
      totalAmount = healthScreeningItems.reduce((sum, item) => {
        const mrp = Number(item?.healthScreeningAmount) || 0;
        const tax = getTaxFromItem(item);
        const taxAmount = mrp * (tax / 100);
        return sum + mrp + taxAmount;
      }, 0);

      totalTax = healthScreeningItems.reduce(
        (sum, item) => sum + getTaxFromItem(item),
        0
      );
    } else if (physiotherapyItems.length > 0) {
      totalAmount = physiotherapyItems.reduce((sum, item) => {
        const mrp = Number(item?.price) || 0;
        const tax = getTaxFromItem(item);
        const taxAmount = mrp * (tax / 100);
        return sum + mrp + taxAmount;
      }, 0);

      totalTax = physiotherapyItems.reduce(
        (sum, item) => sum + getTaxFromItem(item),
        0
      );
    } else if (radioServices.length > 0) {
      totalAmount = radioServices.reduce((sum, item) => {
        const details = item?.radiologyServiceInfo?.serviceDetails || [];
        return (
          sum +
          details.reduce((innerSum, detail) => {
            const mrp = Number(detail?.price) || 0;
            const tax = getTaxFromItem(detail);
            const taxAmount = mrp * (tax / 100);
            return innerSum + mrp + taxAmount;
          }, 0)
        );
      }, 0);

      totalTax = radioServices.reduce((sum, item) => {
        const details = item?.radiologyServiceInfo?.serviceDetails || [];
        return (
          sum +
          details.reduce((innerSum, detail) => {
            return innerSum + getTaxFromItem(detail);
          }, 0)
        );
      }, 0);
    } else if (healthPackageDetails.length > 0) {
      totalAmount = healthPackageDetails.reduce((sum, item) => {
        return sum + (Number(item?.healthPackageAmount) || 0);
      }, 0);

      totalTax = healthPackageDetails.reduce(
        (sum, item) => sum + getTaxFromItem(item),
        0
      );
    } else {
      totalAmount = allOtherServices.reduce((sum, item) => {
        const mrp = Number(item?.price) || 0;
        const tax = getTaxFromItem(item);
        const taxAmount = mrp * (tax / 100);

        return (
          sum + (item?.totalAmount ? Number(item.totalAmount) : mrp + taxAmount)
        );
      }, 0);

      totalTax = allOtherServices.reduce(
        (sum, item) => sum + getTaxFromItem(item),
        0
      );
    }

    const cgstSgst = (totalTax / 2).toFixed(2);

    // 🔹 Update form state
    setFormData((prev) => ({
      ...prev,
      totalAmount: totalAmount.toFixed(2),
      totalAfterDiscount: totalAmount.toFixed(2),
      dueAmount: totalAmount.toFixed(2),
      cgst: cgstSgst,
      sgst: cgstSgst,
    }));
  }, [invoiceInFo]);
  const invoiceHeaderRight = () => (
    <div className={classNames("row mb-4", styles.InvoiceHeading)}>
      <div className="mt-1 col-xl-6">
        <h6>Address:</h6>
        <div className="mt-1">
          <strong>
            {
              invoiceInFo?.appointmentDetails?.patientId?.userId?.address?.city
                ?.name
            }
          </strong>
        </div>
        <div className="mt-1">
          {invoiceInFo?.appointmentDetails?.patientId?.userId?.address ? (
            <>
              {invoiceInFo?.appointmentDetails?.patientId?.userId?.address
                ?.plot &&
                `${invoiceInFo?.appointmentDetails?.patientId?.userId?.address?.plot}, `}
              {invoiceInFo?.appointmentDetails?.patientId?.userId?.address
                ?.area &&
                `${invoiceInFo?.appointmentDetails?.patientId?.userId?.address?.area}, `}
              {invoiceInFo?.appointmentDetails?.patientId?.userId?.address
                ?.street &&
                `${invoiceInFo?.appointmentDetails?.patientId?.userId?.address?.street}, `}
              {invoiceInFo?.appointmentDetails?.patientId?.userId?.address
                ?.landMark &&
                `${invoiceInFo?.appointmentDetails?.patientId?.userId?.address?.landMark}, `}
              {invoiceInFo?.appointmentDetails?.patientId?.userId?.address?.city
                ?.name &&
                `${invoiceInFo?.appointmentDetails?.patientId?.userId?.address?.city.name}, `}
              {invoiceInFo?.appointmentDetails?.patientId?.userId?.address
                ?.state?.name &&
                `${invoiceInFo?.appointmentDetails?.patientId?.userId?.address?.state.name}, `}
              {invoiceInFo?.appointmentDetails?.patientId?.userId?.address
                ?.country?.name &&
                `${invoiceInFo?.appointmentDetails?.patientId?.userId?.address?.country.name}, `}
              {invoiceInFo?.appointmentDetails?.patientId?.userId?.address
                ?.pin &&
                invoiceInFo?.appointmentDetails?.patientId?.userId?.address
                  ?.pin}
            </>
          ) : (
            "N/A"
          )}
        </div>
        <div className="mt-1">
          Name:{invoiceInFo?.appointmentDetails?.patientName}
        </div>
        <div className="mt-1">
          Email: {invoiceInFo?.appointmentDetails?.patientId?.userId?.email}
        </div>
        <div className="mt-1">
          Phone: {invoiceInFo?.appointmentDetails?.patientId?.userId?.phone}
        </div>
      </div>
      <div className="mt-1 col-sm-6 d-flex justify-content-end">
        <div className="row">
          <div className="col-sm-9">
            <div className="brand-logo mb-3">
              {clinic?.hospitalInfo?.pdfHeader && (
                <img
                  src={clinic?.hospitalInfo?.pdfHeader}
                  className="img-fluid"
                  alt=""
                  style={{ width: "270px" }}
                />
              )}
            </div>
            <span className="d-block">
              {clinic?.clinicAddress ? (
                <>
                  {clinic.clinicAddress.plot &&
                    `${clinic.clinicAddress.plot}, `}
                  {clinic.clinicAddress.area &&
                    `${clinic.clinicAddress.area}, `}
                  {clinic.clinicAddress.street &&
                    `${clinic.clinicAddress.street}, `}
                  {clinic.clinicAddress.landMark &&
                    `${clinic.clinicAddress.landMark}, `}
                  {clinic.clinicAddress.city?.name &&
                    `${clinic.clinicAddress.city.name}, `}
                  {clinic?.clinicAddress?.state?.name &&
                    `${clinic.clinicAddress.state.name}, `}
                  {clinic.clinicAddress.country?.name &&
                    `${clinic.clinicAddress.country.name}, `}
                  {clinic.clinicAddress.pin && clinic.clinicAddress.pin}
                </>
              ) : (
                "N/A"
              )}
            </span>
            <small className="d-block mt-1">
              <strong>Website</strong> : {clinic?.hospitalInfo?.web_address}
            </small>
            <small className="d-block mt-1">
              <strong>Contact No</strong> :{clinic?.phone}
            </small>
            <small className="d-block mt-1">
              <strong>GSTIN</strong> : {clinic?.accountInfo?.gst}
            </small>
          </div>
          <div className="col-sm-3 text-end">
            <img
              src={clinic?.hospitalInfo?.ISOLogo}
              className="img-fluid width110"
              alt=""
              style={{ width: "70px" }}
            />
          </div>
        </div>
      </div>
    </div>
  );

  const invoiceHeaderLeft = () => (
    <div className={classNames("row mb-4", styles.InvoiceHeading)}>
      <div className="mt-1 col-sm-6">
        <div className="brand-logo mb-3">
          <div
            style={{
              backgroundImage: `url(${clinic?.hospitalInfo?.pdfHeader})`,
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
              width: "350px",
              height: "70px",
              objectFit: "cover",
            }}
          ></div>
        </div>
        <span className="d-block">
          {clinic?.clinicAddress ? (
            <>
              {clinic.clinicAddress.plot && `${clinic.clinicAddress.plot}, `}
              {clinic.clinicAddress.area && `${clinic.clinicAddress.area}, `}
              {clinic.clinicAddress.street &&
                `${clinic.clinicAddress.street}, `}
              {clinic.clinicAddress.landMark &&
                `${clinic.clinicAddress.landMark}, `}
              {clinic.clinicAddress.city?.name &&
                `${clinic.clinicAddress.city.name}, `}
              {clinic?.clinicAddress?.state?.name &&
                `${clinic.clinicAddress.state.name}, `}
              {clinic.clinicAddress.country?.name &&
                `${clinic.clinicAddress.country.name}, `}
              {clinic.clinicAddress.pin && clinic.clinicAddress.pin}
            </>
          ) : (
            "N/A"
          )}
        </span>
        <small className="d-block mt-1">
          <strong>Website</strong> : {clinic?.hospitalInfo?.web_address}
        </small>
        <small className="d-block mt-1">
          <strong>Contact No</strong> :{clinic?.phone}
        </small>
        <small className="d-block mt-1">
          <strong>GSTIN</strong> : {clinic?.accountInfo?.gst}
        </small>
      </div>
      <div className="mt-1 col-xl-6 d-flex justify-content-end">
        <div className="row">
          <div className="col-sm-9">
            <h6>Address:</h6>
            <div className="mt-1">
              <strong>
                {
                  invoiceInFo?.appointmentDetails?.patientId?.userId?.address
                    ?.city?.name
                }
              </strong>
            </div>
            <div className="mt-1">
              {" "}
              {invoiceInFo?.appointmentDetails?.patientId?.userId?.address ? (
                <>
                  {invoiceInFo?.appointmentDetails?.patientId?.userId?.address
                    ?.plot &&
                    `${invoiceInFo?.appointmentDetails?.patientId?.userId?.address?.plot}, `}
                  {invoiceInFo?.appointmentDetails?.patientId?.userId?.address
                    ?.area &&
                    `${invoiceInFo?.appointmentDetails?.patientId?.userId?.address?.area}, `}
                  {invoiceInFo?.appointmentDetails?.patientId?.userId?.address
                    ?.street &&
                    `${invoiceInFo?.appointmentDetails?.patientId?.userId?.address?.street}, `}
                  {invoiceInFo?.appointmentDetails?.patientId?.userId?.address
                    ?.landMark &&
                    `${invoiceInFo?.appointmentDetails?.patientId?.userId?.address?.landMark}, `}
                  {invoiceInFo?.appointmentDetails?.patientId?.userId?.address
                    ?.city?.name &&
                    `${invoiceInFo?.appointmentDetails?.patientId?.userId?.address?.city.name}, `}
                  {invoiceInFo?.appointmentDetails?.patientId?.userId?.address
                    ?.state?.name &&
                    `${invoiceInFo?.appointmentDetails?.patientId?.userId?.address?.state.name}, `}
                  {invoiceInFo?.appointmentDetails?.patientId?.userId?.address
                    ?.country?.name &&
                    `${invoiceInFo?.appointmentDetails?.patientId?.userId?.address?.country.name}, `}
                  {invoiceInFo?.appointmentDetails?.patientId?.userId?.address
                    ?.pin &&
                    invoiceInFo?.appointmentDetails?.patientId?.userId?.address
                      ?.pin}
                </>
              ) : (
                "N/A"
              )}
            </div>
            <div className="mt-1">
              Name: {invoiceInFo?.appointmentDetails?.patientName}
            </div>
            <div className="mt-1">
              Email: {invoiceInFo?.appointmentDetails?.patientId?.userId?.email}
            </div>
            <div className="mt-1">
              Phone: {invoiceInFo?.appointmentDetails?.patientId?.userId?.phone}
            </div>
          </div>
          <div className="col-sm-3 text-end">
            {clinic?.hospitalInfo?.displayISOlogo && (
              <div
                style={{
                  backgroundImage: `url(${clinic.hospitalInfo.ISOLogo})`,
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "center",
                  width: "52px",
                  height: "60px",
                  objectFit: "cover",
                }}
              ></div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const invoiceHeaderCenter = () => (
    <div className={classNames("row mb-4", styles.InvoiceHeading)}>
      <div className="mt-1 col-sm-9 d-flex justify-content-between">
        <div className="brand-logo mb-3">
          <img
            className="logo-abbr me-2"
            src={clinic?.hospitalInfo?.pdfHeader}
            alt=""
          />
        </div>
      </div>
      <div className="mt-1 col-sm-3 text-end">
        {clinic?.hospitalInfo?.displayISOlogo && (
          <img
            src={clinic.hospitalInfo.ISOLogo}
            className="img-fluid width110"
            alt=""
            style={{ width: "70px" }}
          />
        )}
      </div>
      <div className="mt-1 col-sm-12">
        <span className="d-block">
          {clinic?.clinicAddress ? (
            <>
              {clinic.clinicAddress.plot && `${clinic.clinicAddress.plot}, `}
              {clinic.clinicAddress.area && `${clinic.clinicAddress.area}, `}
              {clinic.clinicAddress.street &&
                `${clinic.clinicAddress.street}, `}
              {clinic.clinicAddress.landMark &&
                `${clinic.clinicAddress.landMark}, `}
              {clinic.clinicAddress.city?.name &&
                `${clinic.clinicAddress.city.name}, `}
              {clinic?.clinicAddress?.state?.name &&
                `${clinic.clinicAddress.state.name}, `}
              {clinic.clinicAddress.country?.name &&
                `${clinic.clinicAddress.country.name}, `}
              {clinic.clinicAddress.pin && clinic.clinicAddress.pin}
            </>
          ) : (
            "N/A"
          )}
        </span>
        <small className="d-block mt-1">
          <strong>Website</strong> : {clinic?.hospitalInfo?.web_address}
        </small>
        <small className="d-block mt-1">
          <strong>Contact No</strong> :{clinic?.phone}
        </small>
        <small className="d-block mt-1">
          <strong>GSTIN</strong> : {clinic?.accountInfo?.gst}
        </small>
      </div>
      <div className=" col-sm-12 mt-3 mb-3">
        <div style={{ backgroundColor: "#40be47", padding: "1px" }}></div>
      </div>
      <div className="mt-1 col-xl-12">
        <small className="mt-1 ms-2">
          <strong>Address: </strong>{" "}
          {invoiceInFo?.appointmentDetails?.patientId?.userId?.address ? (
            <>
              {invoiceInFo?.appointmentDetails?.patientId?.userId?.address
                ?.plot &&
                `${invoiceInFo?.appointmentDetails?.patientId?.userId?.address?.plot}, `}
              {invoiceInFo?.appointmentDetails?.patientId?.userId?.address
                ?.area &&
                `${invoiceInFo?.appointmentDetails?.patientId?.userId?.address?.area}, `}
              {invoiceInFo?.appointmentDetails?.patientId?.userId?.address
                ?.street &&
                `${invoiceInFo?.appointmentDetails?.patientId?.userId?.address?.street}, `}
              {invoiceInFo?.appointmentDetails?.patientId?.userId?.address
                ?.landMark &&
                `${invoiceInFo?.appointmentDetails?.patientId?.userId?.address?.landMark}, `}
              {invoiceInFo?.appointmentDetails?.patientId?.userId?.address?.city
                ?.name &&
                `${invoiceInFo?.appointmentDetails?.patientId?.userId?.address?.city.name}, `}
              {invoiceInFo?.appointmentDetails?.patientId?.userId?.address
                ?.state?.name &&
                `${invoiceInFo?.appointmentDetails?.patientId?.userId?.address?.state.name}, `}
              {invoiceInFo?.appointmentDetails?.patientId?.userId?.address
                ?.country?.name &&
                `${invoiceInFo?.appointmentDetails?.patientId?.userId?.address?.country.name}, `}
              {invoiceInFo?.appointmentDetails?.patientId?.userId?.address
                ?.pin &&
                invoiceInFo?.appointmentDetails?.patientId?.userId?.address
                  ?.pin}
            </>
          ) : (
            "N/A"
          )}
        </small>
        <small className="mt-1 ms-2">
          <strong>Name: </strong>
          {invoiceInFo?.appointmentDetails?.patientName}
        </small>
        <small className="mt-1 ms-2">
          <strong>Email: </strong>
          {invoiceInFo?.appointmentDetails?.patientId?.userId?.email}
        </small>
        ,
        <small className="mt-1 ms-2">
          <strong>Phone: </strong>
          {invoiceInFo?.appointmentDetails?.patientId?.userId?.phone}
        </small>
        ,
      </div>
    </div>
  );

  const invoiceHeaderFullWidth = () => (
    <div className={classNames("row mb-4", styles.InvoiceHeading)}>
      <div className="mt-1 col-sm-5">
        <div className="brand-logo mb-3">
          <img
            className="logo-abbr me-2"
            src={clinic?.hospitalInfo?.pdfHeader}
            alt=""
          />
        </div>
      </div>
      <div className="mt-1 col-sm-5">
        <span className="d-block">
          {clinic?.clinicAddress ? (
            <>
              {clinic.clinicAddress.plot && `${clinic.clinicAddress.plot}, `}
              {clinic.clinicAddress.area && `${clinic.clinicAddress.area}, `}
              {clinic.clinicAddress.street &&
                `${clinic.clinicAddress.street}, `}
              {clinic.clinicAddress.landMark &&
                `${clinic.clinicAddress.landMark}, `}
              {clinic.clinicAddress.city?.name &&
                `${clinic.clinicAddress.city.name}, `}
              {clinic?.clinicAddress?.state?.name &&
                `${clinic.clinicAddress.state.name}, `}
              {clinic.clinicAddress.country?.name &&
                `${clinic.clinicAddress.country.name}, `}
              {clinic.clinicAddress.pin && clinic.clinicAddress.pin}
            </>
          ) : (
            "N/A"
          )}
        </span>
        <small className="d-block mt-1">
          <strong>Website</strong> : {clinic?.hospitalInfo?.web_address}
        </small>
        <small className="d-block mt-1">
          <strong>Contact No</strong> :{clinic?.phone}
        </small>
        <small className="d-block mt-1">
          <strong>GSTIN</strong> : {clinic?.accountInfo?.gst}
        </small>
      </div>
      <div className="mt-1 col-sm-2 text-end">
        {clinic?.hospitalInfo?.displayISOlogo && (
          <img
            src={clinic?.hospitalInfo?.ISOLogo}
            className="img-fluid width110"
            alt=""
            style={{ width: "70px" }}
          />
        )}
      </div>

      <div className=" col-sm-12 mt-3 mb-3">
        <div style={{ backgroundColor: "#40be47", padding: "1px" }}></div>
      </div>
      <div className="mt-1 col-xl-12">
        <small className="mt-1 ms-2">
          <strong>Address: </strong>{" "}
          {invoiceInFo?.appointmentDetails?.patientId?.userId?.address ? (
            <>
              {invoiceInFo?.appointmentDetails?.patientId?.userId?.address
                ?.plot &&
                `${invoiceInFo?.appointmentDetails?.patientId?.userId?.address?.plot}, `}
              {invoiceInFo?.appointmentDetails?.patientId?.userId?.address
                ?.area &&
                `${invoiceInFo?.appointmentDetails?.patientId?.userId?.address?.area}, `}
              {invoiceInFo?.appointmentDetails?.patientId?.userId?.address
                ?.street &&
                `${invoiceInFo?.appointmentDetails?.patientId?.userId?.address?.street}, `}
              {invoiceInFo?.appointmentDetails?.patientId?.userId?.address
                ?.landMark &&
                `${invoiceInFo?.appointmentDetails?.patientId?.userId?.address?.landMark}, `}
              {invoiceInFo?.appointmentDetails?.patientId?.userId?.address?.city
                ?.name &&
                `${invoiceInFo?.appointmentDetails?.patientId?.userId?.address?.city.name}, `}
              {invoiceInFo?.appointmentDetails?.patientId?.userId?.address
                ?.state?.name &&
                `${invoiceInFo?.appointmentDetails?.patientId?.userId?.address?.state.name}, `}
              {invoiceInFo?.appointmentDetails?.patientId?.userId?.address
                ?.country?.name &&
                `${invoiceInFo?.appointmentDetails?.patientId?.userId?.address?.country.name}, `}
              {invoiceInFo?.appointmentDetails?.patientId?.userId?.address
                ?.pin &&
                invoiceInFo?.appointmentDetails?.patientId?.userId?.address
                  ?.pin}
            </>
          ) : (
            "N/A"
          )}
        </small>
        <small className="mt-1 ms-2">
          <strong>Name: </strong> {invoiceInFo?.appointmentDetails?.patientName}
        </small>
        <small className="mt-1 ms-2">
          <strong>Email: </strong>{" "}
          {invoiceInFo?.appointmentDetails?.patientId?.userId?.email}
        </small>
        ,
        <small className="mt-1 ms-2">
          <strong>Phone: </strong>{" "}
          {invoiceInFo?.appointmentDetails?.patientId?.userId?.phone}
        </small>
        ,
      </div>
    </div>
  );

  return (
    <Row
      className={showInvoice ? styles.InvoiceWrapper : styles.InvoiceWrapperTwo}
    >
      <div className="col-lg-12">
        <div
          className={classNames(styles.InvoiceCard, "card")}
          ref={invoiceRef}
          style={{ backgroundImage: `url(${clinic?.hospitalInfo?.waterMark})` }}
        >
          <div
            className="card-body mb-auto position-relative"
            style={{ minHeight: "200vh" }}
          >
            {/* Render selected report UI for preview */}
            {reportName === "tatareport" && (
              <TataReport
                clinic={clinic}
                address={address}
                selectedItem={invoiceInFo?.appointmentDetails}
                item={invoiceInFo}
                forPrint={false}
              />
            )}

            {reportName === "curewellreport" && (
              <CurewellReport
                clinic={clinic}
                address={address}
                selectedItem={invoiceInFo?.appointmentDetails}
                item={invoiceInFo}
                forPrint={false}
              />
            )}

            {reportName === "default" && (
              <DefaultReport
                clinic={clinic}
                address={address}
                selectedItem={invoiceInFo?.appointmentDetails}
                item={invoiceInFo}
                forPrint={false}
              />
            )}

          </div>
            
        </div>
      </div>
        <button
          className="btn btn-primary mt-3 text-white"
          onClick={downloadInvoice}
        >
          Download PDF 
        </button>
        
    </Row>
  );
};

export default Invoice;
